import { ref } from "vue";
import zoomSdk from "@zoom/appssdk";
import { useAuthToken } from './useAuthToken';
const configured = ref(false);
const engagementId = ref(null);
let authTokenHandler = null;
let engagementChangeHandler = null;

export function useZoom() {
	let pollInterval = null;

	const AUTH_TOKEN_VARIABLE_NAME = "CoreAccess.authToken";

	const init = async () => {
		try {
			const config = await zoomSdk.config({
				popoutSize: { width: 480, height: 360 },
				capabilities: [
					"openUrl",
					"getAppVariableList",
					"getEngagementContext",
					"getEngagementVariableValue",
					"onEngagementVariableValueChange",
					"onEngagementContextChange",
				]
			});

			const appVars = await zoomSdk.getAppVariableList();
			console.log('Exposed app variables:');
			console.log(appVars);

			const context = await zoomSdk.getEngagementContext();
			console.log('check the context for engagementId');
			console.log('context obj with engagementId:', context);
			engagementId.value = context?.engagementContext?.engagementId;

			if (!engagementId.value) {
				console.warn('No engagementId found in running context');
			}

			configured.value = true;
			return { config, engagementId: engagementId.value };
		} catch (err) {
			console.error("Zoom SDK config failed:", err);
			throw err;
		}
	};

	const getEngagementVariable = async (name) => {
		if (!configured.value) {
			throw new Error("Zoom SDK not configured. Call init() first.");
		}

		if (!engagementId.value) {
			throw new Error("No engagementId available. This app may not be running in an engagement context.");
		}

		const engagementLocalVariable = await zoomSdk.getEngagementVariableValue({
			engagementId: engagementId.value,
			name
		});

		console.log('Got engagement variable vvvvvvvvvvvvvvvvvvvvv');
		console.log(engagementLocalVariable);

		return engagementLocalVariable;
	};

	const fetchAuthToken = async (retries = 3, delayMs = 1500) => {
		const { setAuthToken, getAuthToken } = useAuthToken();

		try {
			if (!engagementId.value) {
				console.warn('Cannot fetch authToken: No engagementId available');
				return null;
			}

			for (let attempt = 1; attempt <= retries; attempt++) {
				const result = await getEngagementVariable(AUTH_TOKEN_VARIABLE_NAME);
				console.log(`fetchAuthToken attempt ${attempt} raw result:`, result);

				const token = result?.variables?.[0]?.value;
				console.log(`extracted token (attempt ${attempt}):`, token);

				if (token) {
					const currentToken = getAuthToken();
					if (token !== currentToken) {
						console.log('Token changed! Old:', currentToken, '→ New:', token);
						setAuthToken(token);
					} else {
						console.log('Token unchanged, skipping update:', token);
					}
					return token;
				}

				if (attempt < retries) {
					console.warn(`authToken empty on attempt ${attempt}/${retries}, retrying in ${delayMs}ms...`);
					await new Promise(resolve => setTimeout(resolve, delayMs));
				}
			}

			console.warn('authToken still empty after all retries');
			return null;
		} catch (error) {
			console.error('Failed to fetch authToken:', error);
			return null;
		}
	};

	const openUrl = async (url) => {
		if (!configured.value) {
			throw new Error("Zoom SDK not configured. Call init() first.");
		}

		try {
			const parsedUrl = new URL(url);

			if (!["https:", "http:"].includes(parsedUrl.protocol)) {
				throw new Error("Invalid URL protocol");
			}

			await zoomSdk.openUrl({ url: parsedUrl.toString() });

		} catch (err) {
			console.error("Failed to open URL:", err);

			throw err;
		}
	};

	const startEngagementPolling = (intervalMs = 5000) => {
		if (pollInterval) return;

		console.log(`Starting engagement polling every ${intervalMs}ms`);

		pollInterval = setInterval(async () => {
			try {
				const context = await zoomSdk.getEngagementContext();
				const newEngagementId = context?.engagementContext?.engagementId;

				if (newEngagementId && newEngagementId !== engagementId.value) {
					console.log('Polling detected engagementId change:', engagementId.value, '→', newEngagementId);
					engagementId.value = newEngagementId;

					const token = await fetchAuthToken();
					console.log('Token refreshed after polling detected transfer:', token);
				} else if (newEngagementId) {
					// Token may not have been set yet at startup — retry if still missing
					const { getAuthToken } = useAuthToken();
					if (!getAuthToken()) {
						console.log('Polling: token still missing, retrying fetchAuthToken...');
						await fetchAuthToken();
					}
				}
			} catch (err) {
				console.warn('Polling getEngagementContext error (non-fatal):', err);
			}
		}, intervalMs);
	};

	const stopEngagementPolling = () => {
		if (pollInterval) {
			clearInterval(pollInterval);
			pollInterval = null;
			console.log('Engagement polling stopped');
		}
	};

	const setupAuthTokenListener = () => {
		if (!configured.value) {
			throw new Error("Zoom SDK not configured. Call init() first.");
		}

		if (!engagementId.value) {
			console.warn('Cannot setup auth token listener: No engagementId available');
			return () => { }; // Return no-op cleanup function
		}

		// const { setAuthToken } = useAuthToken();

		const handleAuthTokenChange = (event) => {
			console.log('Engagement variable change event:', event);

			const token = event?.value ?? event?.variables?.[0]?.value;

			const variableName = event?.name ?? event?.variables?.[0]?.name;

			if (variableName === AUTH_TOKEN_VARIABLE_NAME && token) {
				const { setAuthToken, getAuthToken } = useAuthToken();
				const currentToken = getAuthToken();
				if (token !== currentToken) {
					setAuthToken(token);
					console.log('Auth token updated from listener:', token);
				} else {
					console.log('Listener: token unchanged, skipping update:', token);
				}
			} else {
				console.log('Unmatched event from useZoom:', event);
			}
		};

		const handleEngagementContextChange = async (event) => {
			console.log('Engagement context changed (event):', event);

			const newEngagementId = event?.engagementContext?.engagementId;

			if (newEngagementId && newEngagementId !== engagementId.value) {
				console.log('New engagementId detected via event:', newEngagementId);
				engagementId.value = newEngagementId;

				const token = await fetchAuthToken();
				console.log('Auth token refreshed after context change event:', token);
			}
		};

		authTokenHandler = handleAuthTokenChange;
		engagementChangeHandler = handleEngagementContextChange;

		zoomSdk.addEventListener("onEngagementVariableValueChange", handleAuthTokenChange);
		zoomSdk.addEventListener("onEngagementContextChange", handleEngagementContextChange);

		startEngagementPolling(5000);

		return () => {
			if (authTokenHandler) {
				zoomSdk.removeEventListener("onEngagementVariableValueChange", authTokenHandler);
				authTokenHandler = null;
			}
			if (engagementChangeHandler) {
				zoomSdk.removeEventListener("onEngagementContextChange", engagementChangeHandler);
				engagementChangeHandler = null;
				console.log('Engagement context listener removed');
			}
			stopEngagementPolling();
		};
	};

	return {
		configured,
		engagementId,
		init,
		getEngagementVariable,
		fetchAuthToken,
		setupAuthTokenListener,
		openUrl,
	};
}